import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Alert, Modal, Tag, Progress, Result } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { quizApi } from '../../../api/quiz.api.js';
import { QuestionCard } from '../components/QuestionCard.jsx';
import { QuizResultModal } from '../components/QuizResultModal.jsx';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner.jsx';
import { ErrorState } from '../../../components/common/ErrorState.jsx';
import { ModuleGateNotice } from '../../learning/components/ModuleGateNotice.jsx';
import { PASS_RULE_TEXT, toTenScale } from '../../learning/utils/moduleGate.js';

export function QuizPage() {
  const { quizId } = useParams();
  const navigate = useNavigate();

  const [quizData, setQuizData] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null); // { code, details } khi module chưa mở / đang xây dựng
  const [timeUp, setTimeUp] = useState(false);
  // Chặn nộp 2 lần cho cùng 1 vé làm bài (bấm nộp đúng lúc hết giờ tự nộp)
  const submittedRef = useRef(false);
  const confirmRef = useRef(null);

  const fetchQuiz = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorInfo(null);
    try {
      const res = await quizApi.getQuizForTaking(quizId);
      setQuizData(res.data);
      setSelectedAnswers({});
      setSecondsElapsed(0);
      setTimeUp(false);
      setResult(null);
      submittedRef.current = false;
    } catch (err) {
      setError(err.message || 'Không thể tải đề kiểm tra');
      setErrorInfo({ code: err.code, details: err.details });
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  const timeLimitSeconds = (quizData?.quiz?.timeLimitMinutes || 0) * 60;
  const canAttempt = quizData?.quiz?.canAttempt !== false;

  // Timer đếm thời gian làm bài. Thời gian chính thức do máy chủ tính từ vé làm bài; đồng hồ này để hiển thị và tự nộp
  useEffect(() => {
    if (loading || showResultModal || timeUp || !canAttempt) return undefined;
    const interval = setInterval(() => {
      setSecondsElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [loading, showResultModal, timeUp, canAttempt]);

  const handleSelectOption = (questionId, optionIndex) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };

  const handleSubmit = () => {
    const questions = quizData?.questions || [];
    const answeredCount = Object.keys(selectedAnswers).length;

    if (answeredCount < questions.length) {
      confirmRef.current = Modal.confirm({
        title: 'Bạn chưa trả lời hết các câu hỏi!',
        icon: <ExclamationCircleOutlined />,
        content: `Bạn mới hoàn thành ${answeredCount} / ${questions.length} câu. Bạn có chắc chắn muốn nộp bài ngay không?`,
        okText: 'Vẫn nộp bài',
        cancelText: 'Làm tiếp',
        onOk: () => performSubmit(),
      });
    } else {
      performSubmit();
    }
  };

  const performSubmit = async ({ auto = false } = {}) => {
    if (submittedRef.current) return;
    const formattedAnswers = Object.entries(selectedAnswers).map(([questionId, selectedOptionIndex]) => ({
      questionId,
      selectedOptionIndex,
    }));

    if (formattedAnswers.length === 0) {
      if (auto) {
        setTimeUp(true);
      } else {
        toast.warning('Vui lòng chọn ít nhất một câu trả lời trước khi nộp bài!');
      }
      return;
    }

    submittedRef.current = true;
    setSubmitting(true);
    try {
      const res = await quizApi.submitQuiz(quizId, {
        attemptToken: quizData?.attemptToken,
        answers: formattedAnswers,
      });
      setResult(res.data);
      setShowResultModal(true);
    } catch (err) {
      if (err.code === 'QUIZ_ATTEMPT_EXPIRED') {
        setTimeUp(true);
      } else if (err.code === 'QUIZ_MAX_ATTEMPTS_REACHED' || err.code === 'QUIZ_ATTEMPT_INVALID') {
        toast.error(err.message);
        fetchQuiz();
      } else {
        submittedRef.current = false; // lỗi mạng / máy chủ: cho bấm nộp lại với cùng vé
        toast.error(err.message || 'Lỗi khi nộp bài làm');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Hết giờ: đóng hộp xác nhận (nếu đang mở) và tự nộp phần đã làm
  const performSubmitRef = useRef(performSubmit);
  performSubmitRef.current = performSubmit;
  useEffect(() => {
    if (timeLimitSeconds > 0 && secondsElapsed >= timeLimitSeconds && !submittedRef.current && !timeUp) {
      confirmRef.current?.destroy();
      toast.warning('Đã hết thời gian làm bài, hệ thống tự nộp phần bạn đã làm.');
      performSubmitRef.current({ auto: true });
    }
  }, [secondsElapsed, timeLimitSeconds, timeUp]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <LoadingSpinner minHeight="100vh" tip="Đang tải đề thi trắc nghiệm..." />;
  }

  if (errorInfo?.code === 'MODULE_LOCKED' || errorInfo?.code === 'MODULE_UNDER_CONSTRUCTION') {
    return <ModuleGateNotice code={errorInfo.code} message={error} details={errorInfo.details} />;
  }

  if (error || !quizData?.quiz) {
    return <ErrorState title="Lỗi tải bài test" subTitle={error} onRetry={fetchQuiz} />;
  }

  const { quiz, questions = [] } = quizData;

  if (!canAttempt) {
    return (
      <Result
        status="warning"
        title="Bạn đã dùng hết lượt làm bài kiểm tra này"
        subTitle={`Bài kiểm tra cho phép tối đa ${quiz.maxAttempts} lượt. Điểm cao nhất của bạn: ${toTenScale(quiz.bestScore ?? 0)}/10 (cần trên 5/10 điểm để đạt).`}
        extra={
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Quay lại bài học
          </Button>
        }
      />
    );
  }

  if (timeUp) {
    return (
      <Result
        status="warning"
        title="Đã hết thời gian làm bài"
        subTitle={`Bài kiểm tra giới hạn ${quiz.timeLimitMinutes} phút. Lượt này không được chấm, bạn làm lại đề mới nhé.`}
        extra={[
          <Button key="retry" type="primary" icon={<RedoOutlined />} onClick={fetchQuiz}>
            Làm lại đề mới
          </Button>,
          <Button key="back" onClick={() => navigate(-1)}>
            Quay lại bài học
          </Button>,
        ]}
      />
    );
  }

  const answeredCount = Object.keys(selectedAnswers).length;
  const progressPercent = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;
  const secondsLeft = timeLimitSeconds > 0 ? Math.max(0, timeLimitSeconds - secondsElapsed) : null;

  const guideParts = [`Đề thi gồm ${questions.length} câu hỏi trắc nghiệm`];
  if (timeLimitSeconds > 0) {
    guideParts.push(`làm trong ${quiz.timeLimitMinutes} phút (hết giờ hệ thống tự nộp phần đã làm)`);
  }
  if (quiz.maxAttempts > 0) {
    guideParts.push(`tối đa ${quiz.maxAttempts} lượt (còn ${quiz.attemptsRemaining} lượt)`);
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: 60 }}>
      {/* Top Sticky Header */}
      <header
        style={{
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          padding: '12px 20px',
        }}
      >
        <div
          style={{
            maxWidth: 880,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ flexShrink: 0 }}>
            Thoát
          </Button>

          <div style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a', overflowWrap: 'break-word' }}>
              {quiz.title}
            </h3>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {PASS_RULE_TEXT} • Đã làm: {answeredCount} / {questions.length} câu
              {quiz.maxAttempts > 0 && ` • Lượt ${quiz.attemptsUsed + 1} / ${quiz.maxAttempts}`}
            </div>
          </div>

          {/* Có giới hạn giờ: đếm ngược, còn dưới 1 phút thì đỏ. Không giới hạn: đếm thời gian đã làm */}
          <Tag
            color={secondsLeft !== null && secondsLeft <= 60 ? 'red' : 'blue'}
            icon={<ClockCircleOutlined />}
            style={{ fontSize: 14, padding: '4px 10px', flexShrink: 0, marginInlineEnd: 0 }}
          >
            {formatTimer(secondsLeft !== null ? secondsLeft : secondsElapsed)}
          </Tag>
        </div>

        {/* Progress Bar under header */}
        <div style={{ maxWidth: 880, margin: '8px auto 0' }}>
          <Progress percent={progressPercent} size="small" showInfo={false} strokeColor="#0284c7" />
        </div>
      </header>

      {/* Questions Container */}
      <main style={{ maxWidth: 880, margin: '24px auto', padding: '0 16px' }}>
        <div style={{ marginBottom: 20 }}>
          <Alert
            message="Hướng dẫn làm bài thi:"
            description={`${guideParts.join(', ')}. Hãy đọc kỹ và chọn phương án chính xác nhất. Kết quả và giải thích đáp án sẽ hiển thị ngay sau khi bạn nộp bài.`}
            type="info"
            showIcon
            style={{ borderRadius: 8, backgroundColor: '#ffffff' }}
          />
        </div>

        {questions.map((q, idx) => (
          <QuestionCard
            key={q.id}
            question={q}
            questionIndex={idx}
            selectedOptionIndex={selectedAnswers[q.id]}
            onSelectOption={handleSelectOption}
          />
        ))}

        {/* Bottom Submit Area */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            padding: '20px 24px',
            background: '#ffffff',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            marginTop: 24,
          }}
        >
          <div style={{ fontSize: 14, color: '#475569' }}>
            Số câu đã trả lời:{' '}
            <strong style={{ color: answeredCount === questions.length ? '#16a34a' : '#0284c7' }}>
              {answeredCount} / {questions.length}
            </strong>
          </div>

          <Button
            type="primary"
            size="large"
            icon={<CheckOutlined />}
            loading={submitting}
            onClick={handleSubmit}
            style={{ height: 44, padding: '0 28px', fontWeight: 700 }}
          >
            Nộp Bài Làm
          </Button>
        </div>
      </main>

      {/* Result Modal */}
      <QuizResultModal
        open={showResultModal}
        result={result}
        onRetry={() => {
          setShowResultModal(false);
          fetchQuiz();
        }}
        courseId={quiz.courseId}
        lessonId={quiz.lessonId}
      />
    </div>
  );
}
